import { useLocalStorage } from '@sixfold/app-utils';
import { isNil, notNil } from '@sixfold/typed-primitives';
import { useCallback, useMemo } from 'react';

export interface RecentTour {
  tourId: number;
  transportId?: string;
  shipperTransportNumber?: string;
  fromTo?: string;
  shipperName?: string;
  carrierName?: string;
  createdAt?: string;
}

export interface HomeViewCompany {
  companyId: string;
  companyName?: string;
}

const RECENT_TOURS_KEY = 'backoffice.recentTours';
const RECENT_COMPANIES_KEY = 'backoffice.recentCompanies';
const FAVORITE_COMPANIES_KEY = 'backoffice.favoriteCompanies';
const MAX_RECENT_COUNT = 5;

export function useRecentTours(): RecentTour[] {
  const [tours] = useLocalStorageList<RecentTour>(RECENT_TOURS_KEY);
  return tours;
}

export function useRecentCompanies(): HomeViewCompany[] {
  const [companies] = useLocalStorageList<HomeViewCompany>(RECENT_COMPANIES_KEY);
  return companies;
}

export function useFavoriteCompanies(): [HomeViewCompany[], (company: HomeViewCompany) => void] {
  const [favorites, setFavorites] = useLocalStorageList<HomeViewCompany>(FAVORITE_COMPANIES_KEY);

  const toggleFavoriteCompany = useCallback(
    (freshCompany: HomeViewCompany) => {
      const isPresent = favorites.some((company) => company.companyId === freshCompany.companyId);
      setFavorites(
        isPresent
          ? favorites.filter((existing) => existing.companyId !== freshCompany.companyId)
          : [freshCompany, ...favorites],
      );
    },
    [favorites, setFavorites],
  );

  return [favorites, toggleFavoriteCompany];
}

export function recordRecentTour(tourToAdd: RecentTour) {
  const recentTours = readList(RECENT_TOURS_KEY) as RecentTour[];
  // Remove any earlier entry for this item (so re-opening it moves it back to the top instead of creating a duplicate),
  // put the freshly viewed item at the front, then keep only the 5 most recent.
  const updatedTours = [tourToAdd, ...recentTours.filter((tour) => tour.tourId !== tourToAdd.tourId)].slice(
    0,
    MAX_RECENT_COUNT,
  );

  localStorage.setItem(RECENT_TOURS_KEY, JSON.stringify(updatedTours));
}

export function recordRecentCompany(companyToAdd: HomeViewCompany) {
  const recentCompanies = readList(RECENT_COMPANIES_KEY) as HomeViewCompany[];
  // Remove any earlier entry for this item (so re-opening it moves it back to the top instead of creating a duplicate),
  // put the freshly viewed item at the front, then keep only the 5 most recent.
  const updatedCompanies = [
    companyToAdd,
    ...recentCompanies.filter((company) => company.companyId !== companyToAdd.companyId),
  ].slice(0, MAX_RECENT_COUNT);

  localStorage.setItem(RECENT_COMPANIES_KEY, JSON.stringify(updatedCompanies));
}

// `@sixfold/app-utils`'s useLocalStorage only stores raw strings, so this wraps it with a JSON array layer:
// it decodes the stored string on read and encodes on write.
function useLocalStorageList<T>(key: string): [T[], (value: T[]) => void] {
  const [rawValue, setRawValue] = useLocalStorage(key);

  const list = useMemo(() => deserializeArray<T>(rawValue), [rawValue]);
  const setList = useCallback((value: T[]) => setRawValue(JSON.stringify(value)), [setRawValue]);

  return [list, setList];
}

function readList(key: string): unknown[] {
  try {
    const raw = localStorage.getItem(key);
    if (isNil(raw)) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn(`Failed to read ${key} from localStorage`, error);
    return [];
  }
}

function deserializeArray<T>(raw: string | undefined): T[] {
  if (isNil(raw)) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(notNil) : [];
  } catch {
    return [];
  }
}
